// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {InputEl} from 'common/components/input';
import {SelectEl} from 'common/components/select';
import {ToggleEl} from 'common/components/toggle';
import {ToggleTheme} from 'common/components/toggle/types';
import {TooltipEl} from 'common/components/tooltip';
import {DisplayType} from 'common/components/tooltip/types';
import {InputSize} from 'common/components/input/types';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize} from 'common/components/button/constants';
import {pluralize} from 'common/util/string';
import {TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import groupSizeImg from 'common/assets/images/custom-groups/group-size.svg';

import {BucketType} from '../types';
import type {NumericBucketPane} from './index';

export function UnitsSelect({cmp}: {cmp: NumericBucketPane}) {
  return cmp.helpers.canSelectBucketUnit() ? (
    <SelectEl
      sel='.mp-bucket-pane-input'
      attrs={{
        elref: `filterUnit`,
        'disable-search': true,
        'menu-width': 200,
        'menu-height': 0,
        'selected-option': pluralize(TimeUnitLabels[cmp.unit], true),
        theme: SelectTheme.Tertiary,
        'never-show-caret': true,
        tooltip: {
          text: `Time Unit to be applied to the bucket values`,
        },
        'help-text': `Time Unit to be applied to the bucket values`,
      }}
      props={{
        sections: cmp.helpers.getBucketUnits(),
      }}
      on={{change: cmp.helpers.changeBucketUnit}}
    />
  ) : null;
}

export function EvenGroup({cmp}: {cmp: NumericBucketPane}) {
  const {t} = cmp.getContext(`i18n`);
  return (
    <div sel='.content-area'>
      <div sel='.range-section'>
        <div sel='.input-group'>
          <InputEl
            sel='.mp-bucket-pane-input'
            attrs={{
              'auto-focus': true,
              'pre-text': t(`query-builder.buckets.min`),
              size: InputSize.Medium,
              type: `number`,
              'validation-status': cmp.helpers.getMinValidationStatus(),
            }}
            on={{
              input: cmp.helpers.changeBucketMin,
            }}
            props={{value: cmp.helpers.getMin().toString()}}
            hook={{insert: cmp.helpers.selectDefault}}
          />
          <InputEl
            sel='.mp-bucket-pane-input'
            attrs={{
              'pre-text': t(`query-builder.buckets.max`),
              size: InputSize.Medium,
              type: `number`,
              'validation-status': cmp.helpers.getMaxValidationStatus(),
            }}
            on={{
              input: cmp.helpers.changeBucketMax,
            }}
            props={{value: cmp.helpers.getMax().toString()}}
          />
        </div>
      </div>
      <div sel='.interval-section'>
        <div sel={cmp.helpers.canSelectBucketUnit() ? `.input-group` : `.mp-bucket-pane-field`}>
          <InputEl
            sel='.mp-bucket-pane-input'
            attrs={{
              'help-text': t(`query-builder.buckets.size-tooltip`, {sizeImg: groupSizeImg}),
              'pre-text': t(`query-builder.buckets.size`),
              type: `number`,
              size: InputSize.Medium,
              'validation-status': cmp.helpers.getBucketSizeValidationStatus(),
            }}
            on={{
              input: cmp.helpers.changeBucketSize,
            }}
            props={{
              value: cmp.helpers.getBucketSize().toString(),
            }}
          />
          <UnitsSelect cmp={cmp} />
        </div>
      </div>
    </div>
  );
}

export function UnevenGroup({cmp}: {cmp: NumericBucketPane}) {
  const {t} = cmp.getContext(`i18n`);
  return (
    <div sel='.vertical-input-group .content-area'>
      {cmp.helpers.getUnevenBuckets().map((group, index) => (
        <InputEl
          sel='.mp-bucket-pane-input'
          attrs={{
            'pre-text': t(`query-builder.buckets.value`),
            size: InputSize.Medium,
            type: `number`,
          }}
          on={{
            input: (ev) => cmp.helpers.changeBucketGroup(ev, index),
            focusout: cmp.helpers.sortUnevenBuckets,
          }}
          props={{value: (group ?? ``).toString()}}
          hook={{postpatch: (vnode) => index === 0 && cmp.helpers.selectDefault(vnode)}}
        />
      ))}
      <UnitsSelect cmp={cmp} />
    </div>
  );
}

export function template(this: NumericBucketPane) {
  const {t} = this.getContext(`i18n`);
  const {tooltipErrorMsg} = this.state;

  return this.params.useNewDesign ? (
    <div sel='.new-style'>
      {tooltipErrorMsg && (
        <TooltipEl
          attrs={{
            'always-show': true,
            'hide-arrow': true,
            type: DisplayType.Error,
          }}
        >
          {tooltipErrorMsg}
        </TooltipEl>
      )}
      <ToggleEl
        sel='.toggle-el'
        attrs={{
          theme: ToggleTheme.TabBar,
          options: [
            {
              label: t(`query-builder.buckets.evenly-divided`),
              value: BucketType.Even,
              tooltipLabel: t(`query-builder.buckets.evenly-divided-tooltip`),
            },
            {
              label: t(`query-builder.buckets.varied-sizes`),
              value: BucketType.Varied,
              tooltipLabel: t(`query-builder.buckets.varied-sizes-tooltip`),
            },
            {
              label: t(`query-builder.buckets.no-buckets`),
              value: BucketType.None,
              tooltipLabel: t(`query-builder.buckets.no-buckets-tooltip`),
            },
          ],
          selected: this.state.currentTab,
        }}
        on={{
          select: this.helpers.handleTabClick,
        }}
      />
      {this.state.currentTab === BucketType.Even && <EvenGroup cmp={this} />}
      {this.state.currentTab === BucketType.Varied && <UnevenGroup cmp={this} />}
      <div sel='.bucket-preview'>{this.getBucketPreview()}</div>
    </div>
  ) : (
    <div sel='.mp-bucket-pane'>
      {tooltipErrorMsg && (
        <TooltipEl
          attrs={{
            'always-show': true,
            'hide-arrow': true,
            type: DisplayType.Error,
          }}
        >
          {tooltipErrorMsg}
        </TooltipEl>
      )}
      <div sel='.mp-bucket-pane-title'>{this.params.header}</div>
      <div sel='.mp-bucket-pane-content'>
        <div sel='.mp-bucket-pane-row'>
          <div sel='.mp-bucket-pane-field'>
            <div sel='.mp-bucket-pane-label'>Interval Size</div>
            <InputEl
              sel='.mp-bucket-pane-input'
              attrs={{
                type: `number`,
                'validation-status': this.helpers.getBucketSizeValidationStatus(),
              }}
              on={{
                input: this.helpers.changeBucketSize,
              }}
              props={{
                value: this.helpers.getBucketSize().toString(),
              }}
              hook={{insert: this.helpers.selectDefault}}
            />
          </div>
          <div sel='.mp-bucket-pane-field'>
            {this.helpers.canSelectBucketUnit() ? (
              <SelectEl
                attrs={{
                  size: ButtonSize.Large,
                  elref: `bucketUnitSelector`,
                }}
                props={{
                  sections: this.helpers.getBucketUnits(),
                }}
                on={{change: this.helpers.changeBucketUnit}}
              />
            ) : null}
          </div>
        </div>
        <div sel='.mp-bucket-pane-row'>
          <div sel='.mp-bucket-pane-field'>
            <div sel='.mp-bucket-pane-label'>Minimum</div>
            <InputEl
              sel='.mp-bucket-pane-input'
              attrs={{
                type: `number`,
                'validation-status': this.helpers.getMinValidationStatus(),
              }}
              on={{
                input: this.helpers.changeBucketMin,
              }}
              props={{value: this.helpers.getMin().toString()}}
            />
          </div>
          <div sel='.mp-bucket-pane-field'>
            <div sel='.mp-bucket-pane-label'>Maximum</div>
            <InputEl
              sel='.mp-bucket-pane-input'
              attrs={{
                type: `number`,
                'validation-status': this.helpers.getMaxValidationStatus(),
              }}
              on={{
                input: this.helpers.changeBucketMax,
              }}
              props={{value: this.helpers.getMax().toString()}}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
